import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { DataFreshnessBadge } from '../components/DataFreshnessBadge'
import {
  PLATFORM_CAPABILITY_COLUMNS,
  buildCapabilityRows,
  type CapabilityAvailability,
  useCompanyPlatformResources,
} from '../platform'
import { integer } from '../utils'

const capabilityTone: Record<CapabilityAvailability, string> = {
  ready: 'is-normal',
  partial: 'is-tight',
  'on-demand': 'is-tight',
  missing: 'is-stockout',
  unsupported: 'is-stagnant',
  blocked: 'is-stockout',
  unknown: 'is-dormant',
}

export function CompanyOverviewPage() {
  const { registry, jd, pinduoduoCatalog } = useCompanyPlatformResources()
  const capabilityRows = useMemo(
    () => registry.data ? buildCapabilityRows(registry.data) : [],
    [registry.data],
  )

  return (
    <div className="page-stack">
      <div className="page-title-row">
        <div>
          <h1>公司经营数据总览</h1>
          <p>查看平台接入范围、指标能力与数据时效</p>
        </div>
        <div className="page-title-actions">
          <Link className="text-link" to="/company/products">公司产品库</Link>
          <Link className="text-link" to="/company/mappings">商品映射</Link>
          <Link className="text-link" to="/">进入京东</Link>
          <Link className="text-link" to="/pinduoduo">进入拼多多</Link>
        </div>
      </div>

      <div className="pdd-caliber-note">
        <strong>跨平台边界：</strong>
        本页只汇总接入覆盖、数据能力和时效。京东店铺交易与拼多多推广归因不是同一口径，不在此相加为公司销售额或利润。
      </div>

      <section className="overview-section">
        <div className="section-heading-row">
          <div><h2>平台与店铺覆盖</h2><p>三个数据模块独立读取，单个资源失败不会隐藏其他结果</p></div>
        </div>
        <div className="pdd-status-grid">
          <article className={registry.error ? 'is-warning' : ''}>
            <span>平台注册表</span>
            {registry.loading && <><strong>加载中</strong><small>正在读取平台与店铺身份…</small></>}
            {registry.error && <><strong>读取失败</strong><small>{registry.error}</small><button type="button" className="text-link" onClick={() => void registry.reload()}>重试</button></>}
            {registry.data && !registry.loading && <>
              <strong>{integer(registry.data.platforms?.length || 0)} 平台 / {integer(registry.data.stores?.length || 0)} 店铺</strong>
              <small>注册表 v{registry.data.schemaVersion || '未标注'}</small>
            </>}
          </article>

          <article className={jd.error ? 'is-warning' : ''}>
            <span>京东数据模块</span>
            {jd.loading && <><strong>加载中</strong><small>正在读取京东元数据…</small></>}
            {jd.error && <><strong>读取失败</strong><small>{jd.error}</small><button type="button" className="text-link" onClick={() => void jd.reload()}>重试</button></>}
            {jd.data && !jd.loading && <>
              <strong>{integer(jd.data.meta.productCounts.spu || 0)} SPU / {integer(jd.data.meta.productCounts.sku || 0)} SKU</strong>
              <small>{jd.data.meta.storeName} · 商品经营明细</small>
              <DataFreshnessBadge date={jd.data.meta.maxDate} compact />
            </>}
          </article>

          <article className={pinduoduoCatalog.error ? 'is-warning' : ''}>
            <span>拼多多数据模块</span>
            {pinduoduoCatalog.loading && <><strong>加载中</strong><small>正在读取拼多多商品主档…</small></>}
            {pinduoduoCatalog.error && <><strong>读取失败</strong><small>{pinduoduoCatalog.error}</small><button type="button" className="text-link" onClick={() => void pinduoduoCatalog.reload()}>重试</button></>}
            {pinduoduoCatalog.data && !pinduoduoCatalog.loading && <>
              <strong>{integer(pinduoduoCatalog.data.meta.productCount)} SPU / {integer(pinduoduoCatalog.data.meta.variantCount)} SKU</strong>
              <small>{pinduoduoCatalog.data.meta.storeName} · 同步商品主档</small>
              <DataFreshnessBadge date={pinduoduoCatalog.data.meta.exportedAt.slice(0, 10)} label="商品主档导出于" mode="on-demand" compact />
            </>}
          </article>
        </div>
      </section>

      <section className="overview-section">
        <div className="section-heading-row">
          <div><h2>数据能力矩阵</h2><p>能力状态以店铺注册表为准，“部分就绪”不等于全店完整口径</p></div>
          {registry.data && <span>{capabilityRows.length} 家店铺</span>}
        </div>
        {registry.loading && <div className="empty-state">正在加载能力矩阵…</div>}
        {registry.error && <div className="empty-state"><p>注册表读取失败，能力矩阵暂不可用。</p><button type="button" className="primary-button" onClick={() => void registry.reload()}>重试注册表</button></div>}
        {registry.data && !registry.loading && capabilityRows.length === 0 && <div className="empty-state">注册表尚未登记可展示的平台店铺。</div>}
        {registry.data && !registry.loading && capabilityRows.length > 0 && <div className="table-responsive">
          <table className="source-table">
            <thead><tr><th>平台 / 店铺</th><th>平台店铺ID</th>{PLATFORM_CAPABILITY_COLUMNS.map((column) => <th key={column.id}>{column.label}</th>)}</tr></thead>
            <tbody>{capabilityRows.map((row) => <tr key={row.key}>
              <td><strong>{row.platformName}</strong><br /><small>{row.storeName}</small></td>
              <td>{row.officialStoreId || '未取得'}</td>
              {PLATFORM_CAPABILITY_COLUMNS.map((column) => {
                const capability = row.capabilities[column.id]
                return <td key={column.id}><span className={`inventory-status ${capabilityTone[capability.status]}`} title={capability.note || capability.label}>{capability.label}</span></td>
              })}
            </tr>)}</tbody>
          </table>
        </div>}
        <p className="methodology-note">能力矩阵只描述“能否以对应粒度分析”；不把推广归因交易、净交易或结算交易冒充全店交易。</p>
      </section>
    </div>
  )
}
